<template>
  <AppShell>
    <!-- Invoice Section -->
    <div class="max-w-2xl mx-auto">
      <div class="mb-6 text-center">
        <h1 class="text-3xl font-extrabold text-white">Invoice</h1>
        <p class="text-ink-muted mt-1">Review your billing details below.</p>
      </div>

      <!-- Invoice Card -->
      <div class="card p-8">
        <h2 class="text-lg font-bold text-white border-b border-line pb-3 mb-6">Billing Summary</h2>

        <div v-if="loading" class="text-center text-ink-muted py-8">Loading...</div>
        <div v-else class="space-y-4">
          <!-- Invoice Info Rows -->
          <div class="flex justify-between items-center border-b border-line pb-4">
            <p class="text-ink-muted">Invoice ID:</p>
            <p class="font-bold text-white">{{ billingDetails.id }}</p>
          </div>
          <div class="flex justify-between items-center border-b border-line pb-4">
            <p class="text-ink-muted">Subscription Type:</p>
            <p class="font-bold capitalize text-white">{{ billingDetails.subscriptionType }}</p>
          </div>
          <div class="flex justify-between items-center border-b border-line pb-4">
            <p class="text-ink-muted">Amount:</p>
            <p class="font-bold text-white">
              <span v-if="discountedAmount" class="line-through text-brand mr-2">Rp {{ billingDetails.amount }}</span>
              <span v-if="discountedAmount">Rp {{ discountedAmount }}</span>
              <span v-else>Rp {{ billingDetails.amount }}</span>
            </p>
          </div>
          <div class="flex justify-between items-center border-b border-line pb-4">
            <p class="text-ink-muted">Status:</p>
            <p :class="statusClass">{{ billingDetails.status }}</p>
          </div>

          <!-- Voucher Input -->
          <div v-if="billingDetails.status === 'pending'" class="pt-2">
            <label for="voucher" class="block text-ink-muted mb-2 text-sm">Voucher Code (Optional)</label>
            <div class="flex items-center gap-3">
              <input id="voucher" v-model="voucherCode" type="text" placeholder="Enter voucher code" class="input" />
              <button class="btn-primary shrink-0" @click="validateVoucher">
                Validate
              </button>
            </div>
            <p v-if="discountMessage" class="mt-2 text-sm text-success">{{ discountMessage }}</p>
            <p v-if="voucherError" class="mt-2 text-sm text-brand">{{ voucherError }}</p>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end pt-4 gap-3">
            <button v-if="billingDetails.status === 'pending'" class="btn-gold" @click="payInvoice">
              Pay Now
            </button>
            <button v-if="billingDetails.status === 'pending'" class="btn-primary" @click="cancelInvoice">
              Cancel Invoice
            </button>
            <button class="btn-secondary" @click="backToProfile">
              Back to Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

export default {
    name: "InvoicePage",
    components: {
        AppShell,
    },
    data() {
        return {
            billingDetails: null,
            loading: true,
            voucherCode: "",
            discountedAmount: null,
            discountMessage: "",
            voucherError: "",
        };
    },
    computed: {
        statusClass() {
            switch (this.billingDetails?.status) {
                case "pending":
                    return "text-yellow-500 font-bold";
                case "completed":
                    return "text-success font-bold";
                case "denied":
                    return "text-brand font-bold";
                default:
                    return "text-ink-faint";
            }
        },
    },
    methods: {
        async fetchBillingDetails(billingId) {
            this.loading = true;
            try {
                const response = await apiClient.get(`/subscription/get/${billingId}`);
                this.billingDetails = response.data;
            } catch (error) {
                console.error("Error fetching billing details:", error);
                toastr.error("Failed to load billing details.", "Error");
            } finally {
                this.loading = false;
            }
        },
        async validateVoucher() {
            this.discountMessage = "";
            this.voucherError = "";
            try {
                const response = await apiClient.get(`/vouchers/validate/${this.voucherCode}`);
                const voucher = response.data;

                if (voucher.isUsed && voucher.usage == "onetime") {
                    this.voucherError = "This voucher has already been used.";
                    return;
                }

                if (voucher.type === "percentage") {
                    const discount = (voucher.value / 100) * this.billingDetails.amount;
                    this.discountedAmount = Math.max(0, this.billingDetails.amount - discount);
                    this.discountMessage = `You received a ${voucher.value}% discount!`;
                } else if (voucher.type === "nominal") {
                    this.discountedAmount = Math.max(0, this.billingDetails.amount - voucher.value);
                    this.discountMessage = `You received a Rp ${voucher.value.toLocaleString()} discount!`;
                }
            } catch (error) {
                console.error("Error validating voucher:", error);
                this.voucherError = "Invalid or unusable voucher code.";
            }
        },
        async payInvoice() {
            try {
                const payload = this.voucherCode ? { voucherCode: this.voucherCode } : {};
                const response = await apiClient.post(`/subscription/pay/${this.billingDetails.id}`, payload);
                const transactionToken = response.data.transactionToken;

                if (transactionToken) {
                    this.launchMidtransSnap(transactionToken);
                } else {
                    toastr.error("Failed to retrieve transaction token.", "Error");
                }
            } catch (error) {
                console.error("Error processing payment:", error);
                toastr.error("An error occurred while processing your request.", "Error");
            }
        },
        launchMidtransSnap(transactionToken) {
            window.snap.pay(transactionToken, {
                onSuccess: (result) => {
                    console.log(result);
                    toastr.success("Payment successful! Thank you for subscribing.", "Success");
                    window.location.reload();
                },
                onPending: (result) => {
                    console.log(result);
                    toastr.warning("Payment is pending. Please complete the payment process.", "Pending");
                },
                onError: (result) => {
                    console.log(result);
                    toastr.error("Payment failed. Please try again.", "Error");
                },
            });
        },
        async cancelInvoice() {
            try {
                await apiClient.post(`/subscription/deny/${this.billingDetails.id}`);
                toastr.success("Invoice cancelled successfully.", "Success");
                this.backToProfile();
            } catch (error) {
                console.error("Error cancelling invoice:", error);
                toastr.error("Failed to cancel the invoice.", "Error");
            }
        },
        backToProfile() {
            this.$router.push("/profile");
        },
        formatDate(date) {
            return new Date(date).toLocaleString();
        },
    },
    mounted() {
        const billingId = this.$route.params.id; // Assume billingId is passed in route params
        this.fetchBillingDetails(billingId);
    },
};
</script>
